import { useEffect, useMemo, useState } from 'react';
import GemImage from '../components/GemImage.jsx';
import { normalizeShape } from '../lib/gemLogic.js';

const STAT_NAMES = ['공격력', '체력', '방어력', '회복력', '치명확률', '치명타피해', '효과저항', '효과적중'];

function makeDraft(gem) {
  return {
    ...gem,
    main_stat: { name: gem.main_stat?.name || '', value: gem.main_stat?.value || '' },
    sub_stats: (gem.sub_stats || []).map((s) => ({ name: s?.name || '', value: s?.value || '' })),
  };
}

export default function GemReviewPage({ gems, onChange, imageSource, t }) {
  const [shapeFilter, setShapeFilter] = useState('전체');
  const [uncertainOnly, setUncertainOnly] = useState(false);
  const [currentId, setCurrentId] = useState(null);

  const valid = useMemo(() => gems.filter((g) => {
    if (['보류', '휴지통'].includes(g.status)) return false;
    if (uncertainOnly && !g.is_uncertain) return false;
    if (shapeFilter !== '전체' && normalizeShape(g.shape) !== shapeFilter) return false;
    return true;
  }), [gems, shapeFilter, uncertainOnly]);

  useEffect(() => {
    if (!valid.length) { setCurrentId(null); return; }
    if (!valid.some((g) => g.id === currentId)) setCurrentId(valid[0].id);
  }, [valid, currentId]);

  const current = valid.find((g) => g.id === currentId) || null;
  const [draft, setDraft] = useState(null);
  useEffect(() => { setDraft(current ? makeDraft(current) : null); }, [current?.id]);

  if (!valid.length || !current || !draft) {
    return (
      <section className="page-section">
        <h2>✏️ {t('review')}</h2>
        <div className="filters-row">
          <select value={shapeFilter} onChange={(e) => setShapeFilter(e.target.value)}>
            <option value="전체">{t('all')}</option><option value="네모">네모</option><option value="세모">세모</option><option value="마름모">마름모</option>
          </select>
          <label><input type="checkbox" checked={uncertainOnly} onChange={(e) => setUncertainOnly(e.target.checked)} /> {t('uncertain')}</label>
        </div>
        <div className="empty-card">조건에 맞는 보석이 없습니다.</div>
      </section>
    );
  }

  const pos = valid.findIndex((g) => g.id === current.id);
  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const setSub = (index, key, value) => setDraft((d) => ({ ...d, sub_stats: d.sub_stats.map((s, i) => i === index ? { ...s, [key]: value } : s) }));

  function save() {
    const nextGem = { ...draft, shape: normalizeShape(draft.shape), is_uncertain: false };
    onChange(gems.map((g) => g.id === current.id ? nextGem : g));
  }

  function trash() {
    onChange(gems.map((g) => g.id === current.id ? { ...g, status: '휴지통' } : g));
  }

  return (
    <section className="page-section">
      <div className="section-heading"><div><h2>✏️ {t('review')}</h2><p>{pos + 1} / {valid.length}</p></div></div>
      <div className="filters-row">
        <select value={shapeFilter} onChange={(e) => setShapeFilter(e.target.value)}>
          <option value="전체">{t('all')}</option><option value="네모">■ 네모</option><option value="세모">▲ 세모</option><option value="마름모">◆ 마름모</option>
        </select>
        <label className="check-label"><input type="checkbox" checked={uncertainOnly} onChange={(e) => setUncertainOnly(e.target.checked)} /> {t('uncertain')}</label>
        <select value={current.id} onChange={(e) => setCurrentId(Number(e.target.value))}>
          {valid.map((g) => <option key={g.id} value={g.id}>#{g.id} · {g.name} · +{g.enhancement} · ★{g.star}</option>)}
        </select>
      </div>

      <div className="review-grid">
        <div className="card image-card">
          <h3>📸 #{current.id}</h3>
          <GemImage fileName={current.file_name} imageSource={imageSource} alt={current.name} />
          <p className="muted mono">{current.file_name}</p>
          <div className="button-row">
            <button className="secondary" disabled={pos <= 0} onClick={() => setCurrentId(valid[pos - 1].id)}>← {t('previous')}</button>
            <button className="secondary" disabled={pos >= valid.length - 1} onClick={() => setCurrentId(valid[pos + 1].id)}>{t('next')} →</button>
          </div>
          {current.equipped_to ? <div className="status-card success">장착: {current.equipped_to}</div> : <div className="status-card">미장착</div>}
          {current.shape_reason && <details><summary>AI shape reason</summary><p>{current.shape_reason}</p></details>}
        </div>

        <div className="card form-card">
          <h3>✏️ 데이터 수정</h3>
          <div className="form-grid two">
            <label>보석 이름<input value={draft.name || ''} onChange={(e) => setField('name', e.target.value)} /></label>
            <label>{t('shape')}
              <select value={normalizeShape(draft.shape)} onChange={(e) => setField('shape', e.target.value)}>
                <option value="네모">네모</option><option value="세모">세모</option><option value="마름모">마름모</option>
              </select>
            </label>
            <label>별<input type="number" min="1" max="6" value={draft.star ?? 6} onChange={(e) => setField('star', Number(e.target.value))} /></label>
            <label>{t('enhancement')}<input type="number" min="0" max="15" value={draft.enhancement ?? 0} onChange={(e) => setField('enhancement', Number(e.target.value))} /></label>
          </div>

          <h4>{t('mainStat')}</h4>
          <div className="form-grid two">
            <label>옵션<select value={draft.main_stat.name} onChange={(e) => setDraft((d) => ({ ...d, main_stat: { ...d.main_stat, name: e.target.value } }))}>
              <option value="">-</option>{STAT_NAMES.map((x) => <option key={x}>{x}</option>)}
            </select></label>
            <label>수치<input value={draft.main_stat.value} onChange={(e) => setDraft((d) => ({ ...d, main_stat: { ...d.main_stat, value: e.target.value } }))} placeholder="68.0%" /></label>
          </div>

          <label>{t('setOption')}<textarea rows="2" value={draft.set_option || ''} onChange={(e) => setField('set_option', e.target.value)} /></label>

          <div className="inline-heading"><h4>{t('subStats')}</h4><button className="small secondary" onClick={() => setField('sub_stats', [...draft.sub_stats, { name: '', value: '' }])}>+ 추가</button></div>
          <div className="substat-editor">
            {draft.sub_stats.map((s, i) => (
              <div className="substat-row" key={i}>
                <select value={s.name} onChange={(e) => setSub(i, 'name', e.target.value)}>
                  <option value="">-</option>{STAT_NAMES.map((x) => <option key={x}>{x}</option>)}
                </select>
                <input value={s.value} onChange={(e) => setSub(i, 'value', e.target.value)} placeholder="13.0%" />
                <button className="small danger" onClick={() => setField('sub_stats', draft.sub_stats.filter((_, idx) => idx !== i))}>×</button>
              </div>
            ))}
          </div>

          <div className="button-row sticky-actions">
            <button onClick={save}>💾 {t('saveChanges')}</button>
            <button className="danger" onClick={trash}>🗑️ {t('moveTrash')}</button>
          </div>
        </div>
      </div>
    </section>
  );
}
